import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  SelectChangeEvent,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Webhook,
  Home,
  AdminPanelSettings,
  Refresh,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface WebhookLog {
  id: number;
  eventId: string;
  subscriptionType: string;
  action: string;
  objectType: string | null;
  objectId: string;
  objectName: string | null;
  propertyName: string | null;
  propertyValue: any;
  occurredAt: string;
  processedAt: string;
  createdAt: string;
  processingStatus: 'success' | 'failed' | 'skipped' | 'no_handler' | null;
}

interface WebhookLogsResponse {
  success: boolean;
  data: {
    logs: WebhookLog[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
}

const SUBSCRIPTION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'object.creation', label: 'Creation' },
  { value: 'object.propertyChange', label: 'Property Change' },
  { value: 'object.deletion', label: 'Deletion' },
  { value: 'object.merge', label: 'Merge' },
];

const PROCESSING_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'no_handler', label: 'No Handler' },
];

// HubSpot Deal Stage ID to Friendly Name mapping
const DEAL_STAGE_NAMES: Record<string, string> = {
  // Appraisal Bid Pipeline
  '962003457': 'Contract Sent (Appraisal Bid)',
  '962003459': 'Contract Signed (Appraisal Bid)',
  '962003460': 'Deal Lost (Appraisal Bid)',
  '962213552': 'Lead Generated (Appraisal Bid)',
  '972145803': 'Lead Assigned (Appraisal Bid)',
  '985365664': 'In Discussions (Appraisal Bid)',
  '985661477': 'Submitted to Bid Portal (Appraisal Bid)',
  '990575212': 'Retainer Paid (Appraisal Bid)',
  '996553027': 'Invoice Sent (Appraisal Bid)',
  // Appraisal Production Pipeline
  '972312367': 'Waiting for Client Documents (Appraisal Prod)',
  '972312368': 'Ready for Model Build Out (Appraisal Prod)',
  '972312369': 'Model Build Out In Process (Appraisal Prod)',
  '972312370': 'Ready for Appraiser (Appraisal Prod)',
  '972312371': 'Report Creation In Progress (Appraisal Prod)',
  '972312372': 'Report In Review (Appraisal Prod)',
  '972312373': 'Report Delivered (Appraisal Prod)',
  '972312374': 'Invoice Sent (Appraisal Prod)',
  '972312375': 'Invoice Paid (Appraisal Prod)',
  '984982787': 'Project Cancelled - No Invoice (Appraisal Prod)',
  // Consulting Bid Pipeline
  '977679140': 'Lead Assigned (Consulting Bid)',
  '977679141': 'Qualified To Buy (Consulting Bid)',
  '977679142': 'Presentation Scheduled (Consulting Bid)',
  '977679143': 'In Discussions (Consulting Bid)',
  '977679144': 'Contract Sent (Consulting Bid)',
  '977679145': 'Contract Signed (Consulting Bid)',
  '977679146': 'Deal Lost (Consulting Bid)',
  '985844480': 'Lead Generated (Consulting Bid)',
  '991588605': 'Invoice Sent (Consulting Bid)',
  '991588616': 'Retainer Paid (Consulting Bid)',
  '1001220417': 'Invoice Sent (Consulting Bid)',
  // Consulting Production Pipeline
  '977672588': 'Project in Progress (Consulting Prod)',
  '977672589': 'Project Complete (Consulting Prod)',
  '985330816': 'Project Cancelled - No Invoice (Consulting Prod)',
  '991551058': 'Ready to Begin (Consulting Prod)',
  '991551059': 'Invoice Sent (Consulting Prod)',
  '991578017': 'Invoice Paid (Consulting Prod)',
  '1023676675': 'Ready for Invoicing (Consulting Prod)',
};

export const HubSpotWebhookLogsPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  // Filter state
  const [subscriptionType, setSubscriptionType] = useState('');
  const [processingStatus, setProcessingStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Build query params
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationModel.page + 1));
    params.append('pageSize', String(paginationModel.pageSize));
    if (subscriptionType) params.append('subscriptionType', subscriptionType);
    if (processingStatus) params.append('processingStatus', processingStatus);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return params.toString();
  };

  // Fetch webhook logs
  const { data: logsData, isLoading, refetch } = useQuery<WebhookLogsResponse>({
    queryKey: ['hubspot-webhook-logs', subscriptionType, processingStatus, startDate, endDate, paginationModel],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/hubspot-webhook-logs/portal/logs?${buildQueryParams()}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch webhook logs');
      }
      return await response.json();
    },
    enabled: !!user?.permissions?.includes('permissions.all'),
  });

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permissions.all permission
  if (!user?.permissions?.includes('permissions.all')) {
    return <Navigate to="/" replace />;
  }

  const logs = logsData?.data?.logs || [];
  const pagination = logsData?.data?.pagination || { page: 1, pageSize: 50, total: 0, totalPages: 0 };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'creation':
        return 'success';
      case 'propertyChange':
        return 'info';
      case 'deletion':
        return 'error';
      case 'merge':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProcessingStatusColor = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'skipped':
        return 'warning';
      case 'no_handler':
        return 'default';
      default:
        return 'default';
    }
  };

  const getProcessingStatusLabel = (status: string | null) => {
    switch (status) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      case 'no_handler':
        return 'No Handler';
      default:
        return '—';
    }
  };

  const handleClearFilters = () => {
    setSubscriptionType('');
    setProcessingStatus('');
    setStartDate('');
    setEndDate('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getActionColor(params.value as string) as any}
          variant="outlined"
        />
      ),
    },
    {
      field: 'processingStatus',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip
            label={getProcessingStatusLabel(params.value as string)}
            size="small"
            color={getProcessingStatusColor(params.value as string) as any}
            variant="filled"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )
      ),
    },
    {
      field: 'objectType',
      headerName: 'Type',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            color="default"
            variant="outlined"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )
      ),
    },
    {
      field: 'objectId',
      headerName: 'Object',
      width: 220,
      renderCell: (params: GridRenderCellParams<WebhookLog>) => {
        const row = params.row;
        if (row.objectName) {
          const truncatedName = row.objectName.length > 25
            ? row.objectName.substring(0, 25) + '...'
            : row.objectName;
          const displayText = `${truncatedName} (${row.objectId})`;
          return (
            <Tooltip title={`${row.objectName} (${row.objectId})`}>
              <Typography variant="body2" noWrap>
                {displayText}
              </Typography>
            </Tooltip>
          );
        }
        return (
          <Typography variant="body2">
            {row.objectId}
          </Typography>
        );
      },
    },
    {
      field: 'propertyName',
      headerName: 'Property',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )
      ),
    },
    {
      field: 'propertyValue',
      headerName: 'Value',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<WebhookLog>) => {
        if (params.value === null || params.value === undefined) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }

        // Check if this is a dealstage property and map to friendly name
        const row = params.row;
        if (row.propertyName === 'dealstage') {
          const stageId = String(params.value);
          const stageName = DEAL_STAGE_NAMES[stageId];
          if (stageName) {
            return (
              <Tooltip title={`Stage ID: ${stageId}`}>
                <Chip
                  label={stageName}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ maxWidth: '100%' }}
                />
              </Tooltip>
            );
          }
          // Fall back to showing the ID if not found in mapping
          return (
            <Tooltip title="Unknown stage ID">
              <Chip
                label={stageId}
                size="small"
                color="default"
                variant="filled"
              />
            </Tooltip>
          );
        }

        const displayValue = typeof params.value === 'object'
          ? JSON.stringify(params.value).substring(0, 50) + (JSON.stringify(params.value).length > 50 ? '...' : '')
          : String(params.value).substring(0, 50) + (String(params.value).length > 50 ? '...' : '');
        return (
          <Tooltip title={typeof params.value === 'object' ? JSON.stringify(params.value, null, 2) : String(params.value)}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
              {displayValue}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'occurredAt',
      headerName: 'Relative Time',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatRelativeTime(params.value as string)}
        </Typography>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ pt: 4, pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Webhook sx={{ mr: 0.5 }} fontSize="inherit" />
            HubSpot Webhook Logs
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Webhook sx={{ fontSize: 48, color: '#ff7a59' }} />
            <Typography variant="h4" component="h1" sx={{ color: '#607D8B' }}>
              HubSpot Webhook Logs
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            View and analyze HubSpot webhook events received by the system (last 7 days)
          </Typography>
        </Box>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FilterList sx={{ color: '#607D8B' }} />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={subscriptionType}
                label="Event Type"
                onChange={(e: SelectChangeEvent) => {
                  setSubscriptionType(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {SUBSCRIPTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={processingStatus}
                label="Status"
                onChange={(e: SelectChangeEvent) => {
                  setProcessingStatus(e.target.value);
                  setPaginationModel({ ...paginationModel, page: 0 });
                }}
              >
                {PROCESSING_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPaginationModel({ ...paginationModel, page: 0 });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                sx={{ color: '#607D8B' }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={`${pagination.total} Total Events`}
              sx={{
                bgcolor: '#607D8B',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                sx={{ color: '#607D8B' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Data Grid */}
        <Paper elevation={2} sx={{ flex: 1, minHeight: 400, mb: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
              <Alert severity="info" sx={{ maxWidth: 400 }}>
                No webhook logs found matching the current filters.
              </Alert>
            </Box>
          ) : (
            <DataGrid
              rows={logs}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.total}
              paginationMode="server"
              sortingMode="server"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'id', sort: 'desc' }],
                },
              }}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/admin/hubspot-webhook-logs/${params.row.id}`)}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
};
